/**
 * Authentication context provider for the SPA.
 * Manages session state, user CRUD (admin), and API key management.
 * Exposes typed methods for consumers via the `useAuth` hook.
 */
import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { 
  authService, 
  LoginResponse, 
  ApiKeyResponse, 
  UserResponse, 
  UserCreateRequest, 
  UserUpdateRequest 
} from '../services/api.service';

// Define the shape of our auth context
/**
 * Publicly exposed authentication context contract.
 */
interface AuthContextType {
  isAuthenticated: boolean;
  user: LoginResponse | null;
  apiKeys: ApiKeyResponse[];
  users: UserResponse[];
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  logoutAllSessions: () => Promise<void>;
  updateApiKey: (serviceName: string, apiKey: string) => Promise<void>;
  updateUserApiKey: (userId: number, serviceName: string, apiKey: string) => Promise<ApiKeyResponse>;
  getAllUsers: () => Promise<void>;
  createUser: (userData: UserCreateRequest) => Promise<UserResponse>;
  updateUser: (userId: number, userData: UserUpdateRequest) => Promise<UserResponse>;
  deleteUser: (userId: number) => Promise<void>;
  getUserApiKeys: (userId: number) => Promise<ApiKeyResponse[]>;
  updateUserApiKeys: (userId: number, keys: Array<{ service_name: string; api_key: string }>) => Promise<void>;
  loading: boolean;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  apiKeys: [],
  users: [],
  login: async () => {},
  logout: async () => {},
  logoutAllSessions: async () => {},
  updateApiKey: async () => {},
  updateUserApiKey: async () => ({} as ApiKeyResponse),
  getAllUsers: async () => {},
  createUser: async () => ({ id: 0, username: '', is_admin: false }),
  updateUser: async () => ({ id: 0, username: '', is_admin: false }),
  deleteUser: async () => {},
  getUserApiKeys: async () => [],
  updateUserApiKeys: async () => {}, // Add default for batch update
  loading: true
});

// Provider component
/**
 * Provides authentication state and actions to all children.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<LoginResponse | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[]>([]);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
          
          // Load API keys
          const keys = await authService.getApiKeys();
          setApiKeys(keys);
          
          // If user is admin, load all users
          if (userData.is_admin) {
            try {
              const allUsers = await authService.getAllUsers();
              setUsers(allUsers);
            } catch (err) {
              console.error('Failed to load users:', err);
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // If auth check fails, clear any stored tokens
        authService.logout();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  /** Authenticate a user and populate user, apiKeys and users (if admin). */
  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      const userData = await authService.login(username, password);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Load API keys after login
      const keys = await authService.getApiKeys();
      setApiKeys(keys);
      
      // If user is admin, load all users
      if (userData.is_admin) {
        try {
          const allUsers = await authService.getAllUsers();
          setUsers(allUsers);
        } catch (err) {
          console.error('Failed to load users:', err);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  /** Logout current session and clear all in-memory state. */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setApiKeys([]);
      setUsers([]);
    }
  }, []);

  // Logout all sessions function
  /** Invalidate all user sessions server-side and clear local state. */
  const logoutAllSessions = useCallback(async () => {
    try {
      await authService.logoutAllSessions();
    } catch (error) {
      console.error('Logout all sessions error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setApiKeys([]);
      setUsers([]);
    }
  }, []);

  // Get all users (admin only) - memoized with useCallback
  /** Fetch and cache all users; only for admins. */
  const getAllUsers = useCallback(async () => {
    if (!user?.is_admin) {
      console.error('Only admins can view all users');
      return;
    }
    
    try {
      setLoading(true);
      const allUsers = await authService.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      console.error('Failed to get users:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.is_admin]); // Only depend on admin status, not setLoading/setUsers
  
  // Create a new user (admin only) - memoized with useCallback
  /** Create a user and append to the local users list. */
  const createUser = useCallback(async (userData: UserCreateRequest): Promise<UserResponse> => {
    if (!user?.is_admin) {
      throw new Error('Only admins can create users');
    }
    
    try {
      setLoading(true);
      const newUser = await authService.createUser(userData);
      
      // Update users list
      setUsers(prev => [...prev, newUser]);
      
      return newUser;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.is_admin]); // Only depend on admin status
  
  // Update a user (admin or self) - memoized with useCallback
  /** Update a user; admins can update anyone, otherwise only self. */
  const updateUser = useCallback(async (userId: number, userData: UserUpdateRequest): Promise<UserResponse> => {
    // Users can update themselves, admins can update anyone
    if (user?.id !== userId && !user?.is_admin) {
      throw new Error('You can only update your own account unless you are an admin');
    }
    
    try {
      setLoading(true);
      const updatedUser = await authService.updateUser(userId, userData);
      
      // Update users list if admin
      if (user?.is_admin) {
        setUsers(prev => 
          prev.map(u => u.id === userId ? updatedUser : u)
        );
      }
      
      // If updating self, also update the current user state
      if (user?.id === userId) {
        setUser(prev => prev ? { ...prev, ...updatedUser } : null);
      }
      
      return updatedUser;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.is_admin]); // Only depend on user ID and admin status
  
  // Delete a user (admin only) - memoized with useCallback
  /** Delete a user; only admins permitted. */
  const deleteUser = useCallback(async (userId: number): Promise<void> => {
    if (!user?.is_admin) {
      throw new Error('Only admins can delete users');
    }
    
    // Prevent deleting yourself
    if (user.id === userId) {
      throw new Error('You cannot delete your own account');
    }
    
    try {
      setLoading(true);
      await authService.deleteUser(userId);
      
      // Update users list
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.is_admin]); // Only depend on user ID and admin status
  
  // Update API key - memoized with useCallback
  /** Create or update the current user's API key for a service. */
  const updateApiKey = useCallback(async (serviceName: string, apiKey: string) => {
    try {
      setLoading(true);
      const updatedKey = await authService.updateApiKey(serviceName, apiKey);
      
      // Update the API keys list
      setApiKeys(prev => {
        const exists = prev.some(k => k.service_name === serviceName);
        if (exists) {
          return prev.map(k => 
            k.service_name === serviceName ? updatedKey : k
          );
        } else {
          return [...prev, updatedKey];
        }
      });
    } catch (error) {
      console.error('Failed to update API key:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // Depend on user ID
  
  // Update API key for a specific user - memoized with useCallback
  /** Update another user's API key (admin only). */
  const updateUserApiKey = useCallback(async (userId: number, serviceName: string, apiKey: string) => {
    try {
      setLoading(true);
      const updatedKey = await authService.updateUserApiKey(userId, serviceName, apiKey);
      
      // We don't need to update the apiKeys state here since this is for other users
      // and we're not tracking their API keys in the global state
      return updatedKey;
    } catch (error) {
      console.error('Failed to update API key for user:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.is_admin]); // Depend on user ID and admin status
  
  // Get specific user API keys (admin or self) - memoized with useCallback
  /** Fetch API keys for a given user; requires admin or same user. */
  const getUserApiKeys = useCallback(async (userId: number): Promise<ApiKeyResponse[]> => {
    console.log(`getUserApiKeys called for user ID: ${userId}`);
    // Removed setLoading(true) - component should manage its own loading state

    // Basic check: Ensure user ID is provided
    if (!userId) {
      console.error("getUserApiKeys: No user ID provided.");
      throw new Error("No user ID provided.");
    }

    // Permission check: Ensure user is logged in and is admin or fetching their own keys
    if (!user || (!user.is_admin && user.id !== userId)) {
      console.error(`getUserApiKeys: Permission denied for user ${user?.id} to fetch keys for user ${userId}.`);
      throw new Error("Permission denied to fetch API keys for this user.");
    }

    console.log(`Current user: ${user.id}, isAdmin: ${user.is_admin}`);
    try {
      console.log(`Calling authService.getUserApiKeys for user ID: ${userId}`);
      const response = await authService.getUserApiKeys(userId);
      console.log("Received API keys from backend:", response);
      return response;
    } catch (error) {
      console.error('Error fetching user API keys:', error);
      // Re-throw the error so the calling component can handle it
      throw error;
    }
    // Removed setLoading(false) - component should manage its own loading state
  }, [user?.id, user?.is_admin]); // Depend on user ID and admin status for permission check
  
  // Update multiple API keys for a specific user (admin only) - memoized
  /** Batch update multiple API keys for a user (admin only). */
  const updateUserApiKeys = useCallback(async (userId: number, keys: Array<{ service_name: string; api_key: string }>): Promise<void> => {
    // Ensure only admins can update other users' keys
    if (user?.id !== userId && !user?.is_admin) {
       throw new Error('Permission denied. Only admins can update other users\' API keys.');
    }
    
    try {
      setLoading(true);
      // Use the batch update service function
      await authService.updateUserApiKeysBatch(userId, keys);
      // Potentially refetch keys for the user if needed, or rely on ManageUserApiKeys page to show success
      console.log(`Successfully called batch update for user ${userId}`);
    } catch (error) {
       console.error('Failed to update user API keys in context:', error);
       throw error; // Re-throw to be caught by the calling component
    } finally {
       setLoading(false);
    }
  }, [user?.id, user?.is_admin]); // Depend on user ID and admin status for permission checks
  
  // Memoize the context value to prevent unnecessary renders
  const contextValue = React.useMemo(() => ({
    // Keep user object here for consumers who need the whole object
    // But the memoization depends on stable primitives below
    isAuthenticated, 
    user, 
    apiKeys, 
    users, 
    login, 
    logout,
    logoutAllSessions,
    updateApiKey,
    updateUserApiKey,
    getAllUsers,
    createUser,
    updateUser,
    deleteUser,
    getUserApiKeys,
    updateUserApiKeys, 
    loading 
  }), [
    // Use stable primitive dependencies for memoization
    isAuthenticated, 
    user?.id, // Depend on id
    user?.is_admin, // Depend on admin status
    apiKeys, 
    users, 
    login, 
    logout,
    logoutAllSessions,
    updateApiKey,
    updateUserApiKey,
    getAllUsers,
    createUser,
    updateUser,
    deleteUser,
    getUserApiKeys,
    updateUserApiKeys,
    loading
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;